import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { store } from '../store';
import { syncLocationData } from '../store/actions/location';
import { fetchProjects } from '../store/actions/projects';
import { LOCATION_SHARING_OPT_IN_KEY } from '../constants/location';
import debug from './debug';

export const LOCATION_BACKGROUND_TASK = 'ELEPLAN_LOCATION_UPDATES';

/** Bump when `startLocationUpdatesAsync` options change so existing installs re-apply policy. */
const LOCATION_BG_OPTIONS_VERSION = 3;
const LOCATION_BG_OPTIONS_VERSION_KEY = '@eleplan/location_bg_options_version';

/**
 * Caregiver location sharing: opportunistic, low-frequency updates — not continuous GPS.
 * Previously used Accuracy.Low (cell-tower only, 100–500m drift) which was too imprecise
 * for the 150m Nearby Search radius used by location visit follow-ups.
 * Accuracy.Balanced (WiFi-assisted, 30–100m) gives reliable place identification
 * without requiring continuous GPS. Cadence is unchanged: 500m / 15 min.
 */
const BG_LOCATION = {
  /** Minimum movement before a new point (meters) */
  distanceIntervalM: 500,
  /** Android min gap between updates (ms) */
  timeIntervalMs: 15 * 60 * 1000,
  /** Batch / defer deliveries on iOS (meters + ms) */
  deferredDistanceM: 500,
  deferredIntervalMs: 15 * 60 * 1000,
} as const;

/** Safety: even if the OS delivers bursts, avoid syncing to the API more than once per interval. */
const MIN_MS_BETWEEN_SERVER_SYNCS = 10 * 60 * 1000;
const LOCATION_BG_LAST_SYNC_KEY = '@eleplan/location_bg_last_server_sync_ms';

let TaskManager: typeof import('expo-task-manager') | null = null;
let Location: typeof import('expo-location') | null = null;

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    TaskManager = require('expo-task-manager') as typeof import('expo-task-manager');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    Location = require('expo-location') as typeof import('expo-location');
  } catch {
    // Native module not available in this runtime.
  }
}

async function getProjectIds(): Promise<string[]> {
  const state: any = store.getState();
  const accountId = state?.session?.account?.account_id;
  const cognitoId = state?.authentication?.user?.cognito_id;
  if (!accountId || !cognitoId) return [];

  let projectRows: any[] = [];
  const fetchRes: any = await store.dispatch(fetchProjects(accountId, cognitoId) as any).catch(() => null);
  if (Array.isArray(fetchRes?.payload)) {
    projectRows = fetchRes.payload;
  }
  if (projectRows.length === 0) {
    projectRows = Object.values(state?.projects?.list ?? {});
  }

  return Array.from(
    new Set(
      projectRows
        .map((p: any) => p?.id ?? p?.project_id)
        .filter((id: any) => id != null)
        .map((id: any) => String(id)),
    ),
  );
}

if (TaskManager && Location) {
  try {
    TaskManager.defineTask(LOCATION_BACKGROUND_TASK, async ({ data, error }) => {
      try {
        if (error) {
          debug.warn('Location background task error', error);
          return;
        }

        const optedIn = await AsyncStorage.getItem(LOCATION_SHARING_OPT_IN_KEY);
        if (optedIn !== 'true') return;

        const updates = (data as any)?.locations as Array<any> | undefined;
        const latest = Array.isArray(updates) && updates.length > 0 ? updates[updates.length - 1] : null;
        const coords = latest?.coords;
        if (!coords) return;

        const projectIds = await getProjectIds();
        if (projectIds.length === 0) return;

        const nowMs = Date.now();
        const lastSyncStr = await AsyncStorage.getItem(LOCATION_BG_LAST_SYNC_KEY);
        const lastSync = lastSyncStr ? parseInt(lastSyncStr, 10) : 0;
        if (lastSync && nowMs - lastSync < MIN_MS_BETWEEN_SERVER_SYNCS) {
          return;
        }

        const payload = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: typeof coords.accuracy === 'number' ? coords.accuracy : null,
          altitude: typeof coords.altitude === 'number' ? coords.altitude : null,
          heading: typeof coords.heading === 'number' ? coords.heading : null,
          speed: typeof coords.speed === 'number' ? coords.speed : null,
          timestamp: new Date(latest.timestamp || Date.now()).toISOString(),
          source: Platform.OS === 'ios' ? 'ios-location-background' : 'android-location-background',
        };

        await Promise.allSettled(
          projectIds.map((pid) => store.dispatch(syncLocationData(pid, payload) as any)),
        );
        await AsyncStorage.setItem(LOCATION_BG_LAST_SYNC_KEY, String(nowMs));
      } catch (err) {
        debug.warn('Location background task failed', err);
      }
    });
  } catch {
    // Task already defined or task manager unavailable in this runtime.
  }
}

export async function registerLocationBackgroundUpdates(): Promise<void> {
  if (Platform.OS === 'web' || !Location || !TaskManager) return;
  try {
    const optedIn = await AsyncStorage.getItem(LOCATION_SHARING_OPT_IN_KEY);
    if (optedIn !== 'true') return;

    const fg = await Location.getForegroundPermissionsAsync();
    const bg = await Location.getBackgroundPermissionsAsync();
    if (fg.status !== 'granted' || bg.status !== 'granted') return;

    const appliedVersion = await AsyncStorage.getItem(LOCATION_BG_OPTIONS_VERSION_KEY);
    const started = await Location.hasStartedLocationUpdatesAsync(LOCATION_BACKGROUND_TASK);
    if (started && appliedVersion === String(LOCATION_BG_OPTIONS_VERSION)) {
      return;
    }
    if (started) {
      await Location.stopLocationUpdatesAsync(LOCATION_BACKGROUND_TASK);
    }

    await Location.startLocationUpdatesAsync(LOCATION_BACKGROUND_TASK, {
      accuracy: Location.Accuracy.Balanced,
      timeInterval: BG_LOCATION.timeIntervalMs,
      distanceInterval: BG_LOCATION.distanceIntervalM,
      deferredUpdatesDistance: BG_LOCATION.deferredDistanceM,
      deferredUpdatesInterval: BG_LOCATION.deferredIntervalMs,
      pausesUpdatesAutomatically: true,
      activityType: Location.ActivityType.Other,
      showsBackgroundLocationIndicator: false,
      foregroundService: Platform.OS === 'android'
        ? {
            notificationTitle: 'Location sharing',
            notificationBody: 'Occasional location updates for your care team.',
            notificationColor: '#2563EB',
          }
        : undefined,
    });
    await AsyncStorage.setItem(LOCATION_BG_OPTIONS_VERSION_KEY, String(LOCATION_BG_OPTIONS_VERSION));
    debug.log('ui', 'Location background updates registered (low-frequency policy)');
  } catch (err) {
    debug.warn('Failed to register location background updates', err);
  }
}

export async function unregisterLocationBackgroundUpdates(): Promise<void> {
  if (Platform.OS === 'web' || !Location) return;
  try {
    const started = await Location.hasStartedLocationUpdatesAsync(LOCATION_BACKGROUND_TASK);
    if (!started) return;
    await Location.stopLocationUpdatesAsync(LOCATION_BACKGROUND_TASK);
    await AsyncStorage.removeItem(LOCATION_BG_OPTIONS_VERSION_KEY);
    debug.log('ui', 'Location background updates unregistered');
  } catch (err) {
    debug.warn('Failed to unregister location background updates', err);
  }
}

/**
 * Report one location point right now, for a single plan.
 *
 * The background task reports on MOVEMENT (500m) or a 15-minute floor,
 * whichever comes first — which is the right policy for a passive signal and
 * the wrong one for a caregiver who just asked. A stationary phone can
 * legitimately go many hours without emitting a point, and the wellness rollup
 * only calls location `source_offline` after 24 hours, so "Out & about" spent
 * that whole window saying "home so far" on evidence that had gone quiet.
 *
 * This is the on-demand counterpart. It runs on the RECIPIENT's device, woken
 * by the same `wellness_check_now` push that already triggers a health sync —
 * never on the caregiver's, which is the whole point: reading position on the
 * viewing device would file the caregiver's whereabouts as the recipient's.
 *
 * Deliberately quiet about failure. The caller is a background wake with no UI
 * attached, and a permission the user hasn't granted is a normal state, not an
 * error worth surfacing.
 *
 * @returns true when a point was actually reported.
 */
export async function reportLocationNow(projectId: number | string): Promise<boolean> {
  if (Platform.OS === 'web' || !Location) return false;
  try {
    // Same opt-in gate the background task honours. Someone who turned
    // location sharing off must not be locatable by a check-now.
    const optedIn = await AsyncStorage.getItem(LOCATION_SHARING_OPT_IN_KEY);
    if (optedIn !== 'true') return false;

    // Foreground permission is enough for a one-shot read while the app is
    // awake — this deliberately does NOT require the Always grant the
    // background task needs, so a "While Using" install still answers.
    const { status } = await Location.getForegroundPermissionsAsync();
    if (status !== 'granted') return false;

    const position = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.Balanced,
    });
    const coords = position?.coords;
    if (!coords) return false;

    await store.dispatch(syncLocationData(projectId, {
      latitude: coords.latitude,
      longitude: coords.longitude,
      accuracy: typeof coords.accuracy === 'number' ? coords.accuracy : null,
      altitude: typeof coords.altitude === 'number' ? coords.altitude : null,
      heading: typeof coords.heading === 'number' ? coords.heading : null,
      speed: typeof coords.speed === 'number' ? coords.speed : null,
      timestamp: new Date(position.timestamp || Date.now()).toISOString(),
      // Distinct from the background sources so the origin of a point stays
      // legible downstream — this one was asked for, not observed.
      source: Platform.OS === 'ios' ? 'ios-location-on-demand' : 'android-location-on-demand',
    }) as any);
    return true;
  } catch (err) {
    debug.warn('On-demand location report failed', err);
    return false;
  }
}
